import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { cx } from '@/styled-system/css';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import type { Photo } from 'app/types/commonTypes.type';
import { buildResponsiveImageAttrs } from 'app/shared/utils/fedPhotos';
import { buildListingPhotoAlt } from 'app/shared/utils/listingUtils';
import {
  FullscreenOverlayBackdrop,
  FullscreenOverlayBody,
  FullscreenOverlayContent,
  FullscreenOverlayHeader,
  FullscreenOverlayPortal,
  FullscreenOverlayRoot,
} from '../../../ui/FullscreenOverlay';
import { ChevronLeftIcon, ChevronRightIcon } from '../../../ui/icons';
import * as styles from './photoLightbox.styles';

type PhotoLightboxProps = {
  photos: Array<Photo>;
  initialIndex: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  headerActions?: ReactNode;
};

export const PhotoLightbox = ({ photos, initialIndex, open, onOpenChange, headerActions }: PhotoLightboxProps) => {
  const displayName = useSelector((state: RootReduxState) => state.currentListingDetails?.currentListing?.displayName);
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const trackRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);

  // Reset scroll position when lightbox opens with a new initialIndex
  const prevOpenRef = useRef(open);
  if (open && !prevOpenRef.current) {
    setCurrentIndex(initialIndex);
  }
  prevOpenRef.current = open;

  // Scroll to initialIndex when lightbox opens
  useEffect(() => {
    if (!open) return;
    const track = trackRef.current;
    if (!track) return;

    requestAnimationFrame(() => {
      isScrollingRef.current = true;
      track.scrollTo({ left: initialIndex * track.clientWidth, behavior: 'instant' });
      requestAnimationFrame(() => {
        isScrollingRef.current = false;
      });
    });
  }, [open, initialIndex]);

  // Sync currentIndex from scroll position
  useEffect(() => {
    const track = trackRef.current;
    if (!open || !track) return;

    let rafId: number;
    const handleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        if (isScrollingRef.current) return;
        const index = Math.round(track.scrollLeft / track.clientWidth);
        setCurrentIndex(index);
      });
    };

    track.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      track.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
    };
  }, [open]);

  const scrollToIndex = useCallback(
    (index: number) => {
      const track = trackRef.current;
      if (!track) return;
      const clamped = Math.max(0, Math.min(index, photos.length - 1));
      isScrollingRef.current = true;
      track.scrollTo({ left: clamped * track.clientWidth, behavior: 'instant' });
      setCurrentIndex(clamped);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          isScrollingRef.current = false;
        });
      });
    },
    [photos.length],
  );

  const goToNext = useCallback(() => {
    scrollToIndex(currentIndex + 1);
  }, [currentIndex, scrollToIndex]);

  const goToPrev = useCallback(() => {
    scrollToIndex(currentIndex - 1);
  }, [currentIndex, scrollToIndex]);

  useEffect(() => {
    if (!open) return;

    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrev();
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [open, goToNext, goToPrev]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('button') || target.tagName === 'IMG') {
        return;
      }
      onOpenChange(false);
    },
    [onOpenChange],
  );

  return (
    <FullscreenOverlayRoot open={open} onOpenChange={onOpenChange}>
      <FullscreenOverlayPortal>
        <FullscreenOverlayBackdrop>
          <FullscreenOverlayContent
            variant="media"
            className={styles.lightboxContent}
            ariaLabelledBy="lightbox-counter"
          >
            <FullscreenOverlayHeader layout="floating" variant="media" actions={headerActions} />

            <FullscreenOverlayBody variant="media" padding="edge-to-edge">
              <div
                className={styles.lightboxContainer}
                role="region"
                aria-roledescription="carousel"
                aria-label="Photo lightbox"
                tabIndex={0}
                onClick={handleOverlayClick}
              >
                <div ref={trackRef} className={styles.lightboxTrack}>
                  {photos.map((photo, i) => {
                    const imageAttrs = buildResponsiveImageAttrs(photo.url, 1152, [576, 1152, 1536]);

                    return (
                      <div key={i} className={styles.lightboxSlide}>
                        {imageAttrs ? (
                          <img
                            src={imageAttrs.src}
                            srcSet={imageAttrs.srcSet}
                            sizes="100vw"
                            alt={buildListingPhotoAlt(displayName, photo.caption, i)}
                            className={styles.lightboxImage}
                            loading={Math.abs(i - currentIndex) <= 1 ? 'eager' : 'lazy'}
                            draggable={false}
                          />
                        ) : (
                          <img
                            src={photo.url}
                            alt={buildListingPhotoAlt(displayName, photo.caption, i)}
                            className={styles.lightboxImage}
                            loading={Math.abs(i - currentIndex) <= 1 ? 'eager' : 'lazy'}
                            draggable={false}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>

                <button
                  className={cx(styles.arrowButton, styles.arrowButtonPrev)}
                  onClick={(e) => {
                    e.stopPropagation();
                    goToPrev();
                  }}
                  disabled={currentIndex === 0}
                  aria-label="Previous photo"
                >
                  <ChevronLeftIcon />
                </button>
                <button
                  className={cx(styles.arrowButton, styles.arrowButtonNext)}
                  onClick={(e) => {
                    e.stopPropagation();
                    goToNext();
                  }}
                  disabled={currentIndex === photos.length - 1}
                  aria-label="Next photo"
                >
                  <ChevronRightIcon />
                </button>

                <div id="lightbox-counter" className={styles.photoCounter} aria-live="polite">
                  {currentIndex + 1} of {photos.length}
                </div>
              </div>
            </FullscreenOverlayBody>
          </FullscreenOverlayContent>
        </FullscreenOverlayBackdrop>
      </FullscreenOverlayPortal>
    </FullscreenOverlayRoot>
  );
};
